import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Menu,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextareaAutosize,
  Tooltip,
  ListItemText,
  ListItemIcon,
  TableSortLabel,
  Collapse,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  GetApp as GetVersionIcon,
  VpnKey as LicenseIcon,
  Refresh as RenewIcon,
  Settings as ConfigIcon,
  Translate as LangIcon,
  FilterList as FilterListIcon,
  Backup
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Implementations = () => {
  const { user: currentUser } = useAuth();
  const [implementations, setImplementations] = useState([]);
  const [filteredImplementations, setFilteredImplementations] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImpl, setSelectedImpl] = useState(null);
  const [configDialog, setConfigDialog] = useState(false);
  const [langDialog, setLangDialog] = useState(false);
  const [backupsDialog, setBackupsDialog] = useState(false);
  const [backupContentDialog, setBackupContentDialog] = useState(false);
  const [configData, setConfigData] = useState('');
  const [langData, setLangData] = useState('');
  const [backupContent, setBackupContent] = useState('');
  const [backupsList, setBackupsList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('client_name');
  const [filters, setFilters] = useState({
    client_name: '',
    product_name: '',
    environment: '',
    version: '',
    license_expiry_date: '',
    status: '',
    modules: [],
  });
  const [formData, setFormData] = useState({
    client_id: '',
    product_id: '',
    environment: '',
    license_code: '',
    license_expiry_date: null,
    url: '',
    status: '',
    version: '',
    modules: [],
  });
  const [selectedBackupFilename, setSelectedBackupFilename] = useState('');

  // State for the actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMenuImpl, setCurrentMenuImpl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    fetchImplementations();
    fetchClients();
    fetchProducts();
    fetchModules();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [implementations, filters, order, orderBy]);

  const fetchImplementations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/implementations`);
      setImplementations(response.data);
      setFilteredImplementations(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...implementations];

    // Apply text filters
    if (filters.client_name) {
      filtered = filtered.filter(impl => 
        impl.client_name.toLowerCase().includes(filters.client_name.toLowerCase()));
    }
    if (filters.product_name) {
      filtered = filtered.filter(impl => 
        impl.product_name.toLowerCase().includes(filters.product_name.toLowerCase()));
    }
    if (filters.environment) {
      filtered = filtered.filter(impl => 
        impl.environment.toLowerCase().includes(filters.environment.toLowerCase()));
    }
    if (filters.version) {
      filtered = filtered.filter(impl => 
        impl.version.toLowerCase().includes(filters.version.toLowerCase()));
    }
    if (filters.status) {
      filtered = filtered.filter(impl => impl.status === filters.status);
    }

    // Apply date filter (greater than or equal)
    if (filters.license_expiry_date) {
      filtered = filtered.filter(impl => {
        if (!impl.license_expiry_date) return false;
        return new Date(impl.license_expiry_date) <= new Date(filters.license_expiry_date);
      });
    }

    // Apply modules filter (multiple selection)
    if (filters.modules && filters.modules.length > 0) {
      filtered = filtered.filter(impl => {
        if (!impl.modules || !impl.modules.length) return false;
        return filters.modules.every(moduleId => 
          impl.modules.some(m => m.id === moduleId)
        );
      });
    }

    // Apply sorting
    filtered = filtered.sort((a, b) => {
      const isAsc = order === 'asc';
      
      // Handle special cases for dates
      if (orderBy === 'license_expiry_date') {
        const dateA = a.license_expiry_date ? new Date(a.license_expiry_date) : new Date(0);
        const dateB = b.license_expiry_date ? new Date(b.license_expiry_date) : new Date(0);
        return isAsc ? dateA - dateB : dateB - dateA;
      }
      
      // Handle modules (count)
      if (orderBy === 'modules') {
        const modulesA = a.modules ? a.modules.length : 0;
        const modulesB = b.modules ? b.modules.length : 0;
        return isAsc ? modulesA - modulesB : modulesB - modulesA;
      }
      
      // Default string comparison
      const valueA = a[orderBy] || '';
      const valueB = b[orderBy] || '';
      
      return isAsc 
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });

    setFilteredImplementations(filtered);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      client_name: '',
      product_name: '',
      environment: '',
      version: '',
      license_expiry_date: '',
      status: '',
      modules: [],
    });
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/modules`);
      setModules(response.data);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
    }
  };

  const handleOpenDialog = (impl = null) => {
    if (impl) {
      setSelectedImpl(impl);
      setFormData({
        ...impl,
        modules: impl.modules?.map(m => m.id) || [],
      });
    } else {
      setSelectedImpl(null);
      setFormData({
        client_id: '',
        product_id: '',
        environment: '',
        license_code: '',
        license_expiry_date: '',
        url: '',
        status: '',
        version: '',
        modules: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImpl(null);
  };

  const handleSubmit = async () => {
    try {
      if (selectedImpl) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/implementations/${selectedImpl.id}`,
          formData
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/implementations`, formData);
      }
      fetchImplementations();
      handleCloseDialog();
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleDelete = async (implId) => {
    if (window.confirm('¿Seguro de elimintar esta implementación?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/implementations/${implId}`);
        fetchImplementations();
      } catch (error) {
        console.error('Ha ocurrido un error:', error);
        alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
      }
    }
  };

  const handleGetVersion = async (implId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/implementations/${implId}/version`
      );
      fetchImplementations();
      alert(`Versión actualizada: ${response.data.version}`);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleGetLicense = async (implId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/implementations/${implId}/license`
      );
      fetchImplementations();
      alert(`Licencia obtenida: ${response.data.license}\nExpiry: ${response.data.expiry_date}`);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleRenewLicense = async (implId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/implementations/${implId}/renew-license`
      );
      fetchImplementations();
      alert('Licencia actualizada exitosamente');
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleGetConfig = async (implId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/implementations/${implId}/config`
      );
      setConfigData(JSON.stringify(response.data, null, 2));
      setSelectedImpl({ id: implId });
      setConfigDialog(true);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleSaveConfig = async () => {
    try {
      let _d = JSON.parse(configData);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/implementations/${selectedImpl.id}/config`,
          { config: _d }
        );
        setConfigDialog(false);
        alert('Config actualizado exitosamente');
      } catch (error) {
        console.error('Ha ocurrido un error:', error);
        alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
      }
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ingrese un JSON válido.');
    }
  };

  const handleGetLang = async (implId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/implementations/${implId}/lang`
      );
      const _d = JSON.stringify(response.data, null, 2);      
      if (_d !== "\"\""){
        setLangData(_d);
      }else{
        setLangData("");
      }
      setSelectedImpl({ id: implId });
      setLangDialog(true);
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleSaveLang = async () => {
    try {
      let _d = JSON.parse(langData);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/implementations/${selectedImpl.id}/lang`,
          { lang: _d }
        );
        setLangDialog(false);
        alert('Lang actualizado exitosamente');
      } catch (error) {
        console.error('Ha ocurrido un error:', error);
        alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
      }
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ingrese un JSON válido.');
    }
  };

  const handleViewBackups = async (implId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/backups?id=${implId}`
      );
      setBackupsList(response.data);
      setBackupsDialog(true);
    } catch (error) {
      console.error('Error al obtener los backups:', error);
      alert('Ha ocurrido un error al obtener los backups.');
    }
  };

  const handleViewBackupContent = async (filename) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/backups/file?filename=${filename}`
      );
      setBackupContent(JSON.stringify(response.data, null, 2));
      setSelectedBackupFilename(filename);
      setBackupContentDialog(true);
    } catch (error) {
      console.error('Error al obtener el contenido del backup:', error);
      alert('Ha ocurrido un error al obtener el contenido del backup.');
    }
  };

  const canModifyImplementation = () => {
    return ['Administrador', 'Sistemas'].includes(currentUser.role);
  };

  const canModifyImplementationConfig = () => {
    return ['Administrador', 'Sistemas','Soporte'].includes(currentUser.role);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      '1': 'Implementado',
      '2': 'En proceso de actualización',
      '3': 'En proceso de implementación',
      '4': 'Dado de baja',
      '5': 'Baja temporal',
    };
    return statusMap[status] || status;
  };

  // Menu handlers
  const handleMenuOpen = (event, impl) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenuImpl(impl);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMenuImpl(null);
  };

  return (
    <div>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 2,
        gap: 1
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }} gutterBottom>
          Implementaciones
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
          {canModifyImplementation() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="medium"
              sx={{ flexGrow: { xs: 1, sm: 0 } }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Agregar implementación</Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Agregar</Box>
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ mb: 1 }}
        >
          {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
        </Button>
        
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Cliente"
                  value={filters.client_name}
                  onChange={(e) => handleFilterChange('client_name', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Producto"
                  value={filters.product_name}
                  onChange={(e) => handleFilterChange('product_name', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Entorno"
                  value={filters.environment}
                  onChange={(e) => handleFilterChange('environment', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Versión"
                  value={filters.version}
                  onChange={(e) => handleFilterChange('version', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Venc. licencia (hasta)"
                  type="date"
                  value={filters.license_expiry_date}
                  onChange={(e) => handleFilterChange('license_expiry_date', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="1">Implementado</MenuItem>
                    <MenuItem value="2">En proceso de actualización</MenuItem>
                    <MenuItem value="3">En proceso de implementación</MenuItem>
                    <MenuItem value="4">Dado de baja</MenuItem>
                    <MenuItem value="5">Baja temporal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={modules}
                  getOptionLabel={(option) => option.name}
                  value={modules.filter(module => filters.modules.includes(module.id))}
                  onChange={(e, newValue) => {
                    handleFilterChange('modules', newValue.map(item => item.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Módulos"
                      placeholder="Seleccionar módulos"
                      size="small"
                    />
                  )}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" onClick={resetFilters}>Limpiar filtros</Button>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* Implementaciones - Vista móvil (cards) */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2, mt: 2 }}>
        {filteredImplementations.map((impl) => (
          <Paper key={impl.id} elevation={2} sx={{ p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {impl.client_name}
              </Typography>
              <Box>
                {canModifyImplementation() && (
                  <>
                    <Tooltip title="Editar" arrow>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(impl)}
                        sx={{ mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar" arrow>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(impl.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Producto:
                </Typography>
                <Typography variant="body2">
                  {impl.product_name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Entorno:
                </Typography>
                <Typography variant="body2">
                  {impl.environment}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Versión:
                </Typography>
                <Typography variant="body2">
                  {impl.version}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Venc. licencia:
                </Typography>
                <Typography variant="body2">
                  {impl.license_expiry_date}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Estado:
                </Typography>
                <Typography variant="body2">
                  {getStatusLabel(impl.status)}
                </Typography>
              </Grid>
            </Grid>
            
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Módulos:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {impl.modules && impl.modules.map((module) => (
                  <Chip
                    key={module.id}
                    label={module.name}
                    size="small"
                    sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                  />
                ))}
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={(event) => handleMenuOpen(event, impl)}
            >
              Gestionar
            </Button>
          </Paper>
        ))}
      </Box>

      {/* Tabla de implementaciones - Vista desktop */}
      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ whiteSpace: 'nowrap' }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('client_name')}
                >
                  Cliente
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'product_name'}
                  direction={orderBy === 'product_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('product_name')}
                >
                  Producto
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'environment'}
                  direction={orderBy === 'environment' ? order : 'asc'}
                  onClick={() => handleRequestSort('environment')}
                >
                  Entorno
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'version'}
                  direction={orderBy === 'version' ? order : 'asc'}
                  onClick={() => handleRequestSort('version')}
                >
                  Versión
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'license_expiry_date'}
                  direction={orderBy === 'license_expiry_date' ? order : 'asc'}
                  onClick={() => handleRequestSort('license_expiry_date')}
                >
                  Venc. licencia
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Estado
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'modules'}
                  direction={orderBy === 'modules' ? order : 'asc'}
                  onClick={() => handleRequestSort('modules')}
                >
                  Módulos
                </TableSortLabel>
              </TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredImplementations.map((impl) => (
              <TableRow key={impl.id} sx={{ whiteSpace: 'nowrap' }}>
                <TableCell>{impl.client_name}</TableCell>
                <TableCell>{impl.product_name}</TableCell>
                <TableCell>{impl.environment}</TableCell>
                <TableCell>{impl.version}</TableCell>
                <TableCell>{impl.license_expiry_date}</TableCell>
                <TableCell>{getStatusLabel(impl.status)}</TableCell>
                <TableCell sx={{ whiteSpace: 'normal' }}>
                  {impl.modules && impl.modules.map((module) => (
                    <Chip 
                      key={module.id} 
                      label={module.name} 
                      size="small" 
                      sx={{ m: 0.2 }} 
                    />
                  ))}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Button
                    aria-label="manage"
                    aria-controls={`actions-menu-${impl.id}`}
                    aria-haspopup="true"
                    onClick={(event) => handleMenuOpen(event, impl)}
                    variant="outlined"
                    size="small"
                  >
                    Gestionar
                  </Button>
                  
                  {canModifyImplementation() && (
                    <>
                      <Tooltip title="Editar" arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog(impl)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar" arrow>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(impl.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {currentMenuImpl && (
        <Menu
          id={`actions-menu-${currentMenuImpl.id}`}
          anchorEl={anchorEl}
          open={isMenuOpen && currentMenuImpl !== null}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'manage-button', // You can link it to the button that opened it
          }}
        >
          <MenuItem
            key="open-url"
            onClick={() => {
              if (currentMenuImpl.url) {
                window.open(currentMenuImpl.url, '_blank', 'noopener noreferrer');
              }
              handleMenuClose();
            }}
            disabled={!currentMenuImpl.url}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Abrir URL</ListItemText>
          </MenuItem>

          {canModifyImplementation() && [
            <MenuItem
              key="get-version"
              onClick={() => {
                handleGetVersion(currentMenuImpl.id);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <GetVersionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Obtener Versión</ListItemText>
            </MenuItem>,
            <MenuItem
              key="get-license"
              onClick={() => {
                handleGetLicense(currentMenuImpl.id);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <LicenseIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Obtener Licencia</ListItemText>
            </MenuItem>,
            <MenuItem
              key="renew-license"
              onClick={() => {
                handleRenewLicense(currentMenuImpl.id);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <RenewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Actualizar Licencia</ListItemText>
            </MenuItem>
          ]}
          
          {canModifyImplementationConfig() && [
            <>
              <MenuItem
                key="get-config"
                onClick={() => {
                  handleGetConfig(currentMenuImpl.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <ConfigIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ver Config</ListItemText>
              </MenuItem>
              <MenuItem
                key="get-lang"
                onClick={() => {
                  handleGetLang(currentMenuImpl.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <LangIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ver Lang</ListItemText>
              </MenuItem>
              <MenuItem
                key="view-backups"
                onClick={() => {
                  handleViewBackups(currentMenuImpl.id);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Backup fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ver Backups</ListItemText>
              </MenuItem>
            </>
          ]}
        </Menu>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedImpl ? 'Editar implementación' : 'Agregar implementación'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={formData.client_id}
                  onChange={(e) =>
                    setFormData({ ...formData, client_id: e.target.value })
                  }
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.internal_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Producto</InputLabel>
                <Select
                  value={formData.product_id}
                  onChange={(e) =>
                    setFormData({ ...formData, product_id: e.target.value })
                  }
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Entorno</InputLabel>
                <Select
                  value={formData.environment}
                  onChange={(e) =>
                    setFormData({ ...formData, environment: e.target.value })
                  }
                >
                  <MenuItem key="Producción" value="Producción">Producción</MenuItem>
                  <MenuItem key="Testing" value="Testing">Testing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <MenuItem key="1" value="1">Implementado</MenuItem>
                  <MenuItem key="2" value="2">En proceso de actualización</MenuItem>
                  <MenuItem key="3" value="3">En proceso de implementación</MenuItem>
                  <MenuItem key="4" value="4">Dado de baja</MenuItem>
                  <MenuItem key="5" value="5">Baja temporal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL"
                value={formData.url}
                onChange={(e) =>
                  setFormData({ ...formData, url: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Licencia"
                value={formData.license_code}
                onChange={(e) =>
                  setFormData({ ...formData, license_code: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vencimiento de licencia"
                type="date"
                value={formData.license_expiry_date? formData.license_expiry_date.split('T')[0] : ''}
                onChange={(e) =>
                  setFormData({ ...formData, license_expiry_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Módulos</Typography>
              <FormGroup>
                {modules.map((module) => (
                  <FormControlLabel
                    key={module.id}
                    control={
                      <Checkbox
                        checked={formData.modules.includes(module.id)}
                        onChange={(e) => {
                          const newModules = e.target.checked
                            ? [...formData.modules, module.id]
                            : formData.modules.filter((id) => id !== module.id);
                          setFormData({ ...formData, modules: newModules });
                        }}
                      />
                    }
                    label={module.name}
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configuración</DialogTitle>
        <DialogContent>
          <TextareaAutosize
            minRows={10}
            style={{ width: '100%', fontFamily: 'monospace' }}
            value={configData}
            onChange={(e) => setConfigData(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveConfig} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={langDialog} onClose={() => setLangDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lang</DialogTitle>
        <DialogContent>
          <TextareaAutosize
            minRows={10}
            style={{ width: '100%', fontFamily: 'monospace' }}
            value={langData}
            onChange={(e) => setLangData(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLangDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveLang} variant="contained">
          Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={backupsDialog} onClose={() => setBackupsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Backups disponibles</DialogTitle>
        <DialogContent>
          {backupsList.length > 0 ? (
            <Grid container spacing={2}>
              {backupsList.map((backup, index) => {
                // Determinar el tipo de backup a partir del nombre del archivo
                const isConfig = backup.filename && backup.filename.includes('_conf.');
                const isLang = backup.filename && backup.filename.includes('_lang.');
                
                // Formatear la fecha como dd/mm/yyyy HH:mm:ss
                const backupDate = new Date(backup.created_at || backup.backup_date);
                const formattedDate = backupDate.toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }).replace(/\//g, '/').replace(',', '');
                
                return (
                  <Grid item xs={6} sm={3} md={1.5} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 3,
                          bgcolor: 'rgba(0, 0, 0, 0.04)'
                        },
                        height: '100%'
                      }}
                      onClick={() => handleViewBackupContent(backup.filename)}
                      title={backup.filename} // Mostrar nombre completo al hacer hover
                    >
                      <Box sx={{ fontSize: 40, color: 'primary.main', mb: 1 }}>
                        {isConfig ? <ConfigIcon fontSize="inherit" /> : isLang ? <LangIcon fontSize="inherit" /> : <Backup fontSize="inherit" />}
                      </Box>
                      <Typography variant="caption" align="center" sx={{ wordBreak: 'break-word' }}>
                        {formattedDate}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography>No hay backups disponibles para esta implementación.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupsDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={backupContentDialog} onClose={() => setBackupContentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Contenido del backup: {selectedBackupFilename}
        </DialogTitle>
        <DialogContent>
          <TextareaAutosize
            minRows={10}
            style={{ width: '100%', fontFamily: 'monospace' }}
            value={backupContent}
            readOnly
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupContentDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Implementations;